<template>
  <div class="min-h-screen bg-gray-900">
    <!-- 公告弹窗 -->
    <AnnouncementModal 
      v-if="showAnnouncementModal && currentAnnouncement"
      :show="showAnnouncementModal"
      :announcement="currentAnnouncement"
      @close="handleAnnouncementClose"
      @never-show="handleNeverShow"
    />
    
    <!-- 群二维码弹窗 -->
    <div v-if="showQrCodeModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click="showQrCodeModal = false">
      <div class="bg-white rounded-lg shadow-xl max-w-sm mx-4 p-6" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900">交流群二维码</h3>
          <button @click="showQrCodeModal = false" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="flex justify-center mb-4">
          <div class="relative">
            <img 
              :src="siteConfig.contactGroupImage" 
              alt="交流群二维码" 
              class="max-w-full max-h-80 object-contain rounded-lg"
              @load="handleImageLoad"
            />
          </div>
        </div>
        <p class="text-sm text-gray-600 text-center">扫码加入交流群</p>
      </div>
    </div>
    
    <!-- 移动端顶部栏 -->
    <div class="md:hidden sticky top-0 z-50 bg-black/95 backdrop-blur-md border-b border-gray-800">
      <div class="px-4 py-3">
        <!-- 移动端顶部栏第一行：Logo和筛选按钮 -->
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center space-x-2">
            <!-- Logo -->
            <div v-if="siteConfig.siteLogo" class="flex items-center">
              <img :src="siteConfig.siteLogo" :alt="siteConfig.siteName" class="h-6 w-auto">
            </div>
            <div class="flex flex-col">
              <h1 class="text-lg font-bold text-white leading-tight">{{ siteConfig.siteName }}</h1>
              <span class="text-xs text-gray-400">夸克/百度网盘资源搜索</span>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <!-- 每日更新按钮 -->
            <button
              v-if="showDailyUpdateEntry"
              @click="goToDailyUpdate"
              class="daily-update-btn text-sm px-3 py-1.5 rounded-full font-medium transition-all duration-200 flex items-center space-x-1 bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30 hover:brightness-110"
            >
              <span>🔥</span>
              <span>每日更新</span>
            </button>
            <button
              @click="$router.push('/agent')"
              class="ai-assistant-entry-mobile"
              aria-label="打开AI助手"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v3M7 8h10a4 4 0 014 4v2a4 4 0 01-4 4H9l-4 3v-3H7a4 4 0 01-4-4v-2a4 4 0 014-4z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 13h.01M15 13h.01"/>
              </svg>
              <span>AI助手</span>
            </button>
            <!-- 资源文档链接 -->
            <a 
              v-if="siteConfig.resourceDocsUrl" 
              :href="siteConfig.resourceDocsUrl" 
              target="_blank" 
              class="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
            >
              文档
            </a>
            <!-- 交流群二维码 -->
            <div v-if="siteConfig.contactGroupImage" class="relative group">
              <button 
                class="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                @click="showQrCodeModal = true"
              >
                交流群
              </button>
              <!-- 二维码弹窗 -->
              <div class="absolute right-0 top-full mt-2 w-40 bg-gray-800 rounded-lg shadow-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <img :src="siteConfig.contactGroupImage" alt="交流群二维码" class="w-full h-auto rounded object-contain max-h-32">
                <p class="text-xs text-gray-400 mt-1 text-center">扫码加入交流群</p>
              </div>
            </div>
            <button
              @click="toggleFilters"
              class="filter-button-mobile"
              :class="{ active: showFilters }"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"/>
              </svg>
            </button>
          </div>
        </div>
        
        <!-- 移动端搜索框 -->
        <form @submit.prevent="handleSearch" class="flex gap-2">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索网盘影视资源..."
            class="search-input-mobile flex-1"
          />
          <button
            type="submit"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            搜索
          </button>
        </form>
        <!-- 移动端网盘类型提示 -->
        <div class="flex items-center justify-center mt-2 space-x-3 text-xs text-gray-400">
          <span class="flex items-center">
            <span class="w-2 h-2 rounded-full bg-yellow-500 mr-1"></span>
            夸克
          </span>
          <span class="flex items-center">
            <span class="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>
            百度
          </span>
        </div>
      </div>
    </div>

    <!-- 桌面端导航栏 -->
    <nav class="nav-container hidden md:block">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-3">
          <div class="flex items-center space-x-4">
            <!-- Logo -->
            <div v-if="siteConfig.siteLogo" class="flex items-center">
              <img :src="siteConfig.siteLogo" :alt="siteConfig.siteName" class="h-8 w-auto">
            </div>
            <div class="flex flex-col">
              <h1 class="text-xl font-bold text-white leading-tight">{{ siteConfig.siteName }}</h1>
              <span class="text-xs text-gray-400">夸克/百度网盘资源搜索引擎</span>
            </div>
          </div>
          <div class="flex-1 max-w-2xl mx-6">
            <form @submit.prevent="handleSearch" class="flex gap-2">
              <div class="flex-1 relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索网盘影视资源..."
                  class="search-input w-full"
                />
                <!-- 网盘类型提示 - 移到搜索框右侧内部 -->
                <div class="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:flex items-center space-x-2 text-xs text-gray-500">
                  <span class="flex items-center">
                    <span class="w-2 h-2 rounded-full bg-yellow-500 mr-1"></span>
                    夸克
                  </span>
                  <span class="flex items-center">
                    <span class="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>
                    百度
                  </span>
                </div>
              </div>
              <button
                type="submit"
                class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200 whitespace-nowrap"
              >
                搜索
              </button>
            </form>
          </div>
          <div class="flex items-center space-x-4">
            <!-- 每日更新按钮 -->
            <button
              v-if="showDailyUpdateEntry"
              @click="goToDailyUpdate"
              class="daily-update-btn px-4 py-2 rounded-full font-medium transition-all duration-200 flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30 hover:brightness-110"
            >
              <span>🔥</span>
              <span>每日更新</span>
            </button>
            <button
              @click="$router.push('/agent')"
              class="ai-assistant-entry"
              aria-label="打开AI助手"
            >
              <span class="ai-assistant-entry-icon" aria-hidden="true">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v3M7 8h10a4 4 0 014 4v2a4 4 0 01-4 4H9l-4 3v-3H7a4 4 0 01-4-4v-2a4 4 0 014-4z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 13h.01M15 13h.01"/>
                </svg>
              </span>
              <span>AI助手</span>
              <span class="ai-assistant-entry-badge">智能找资源</span>
            </button>
            <!-- 资源文档链接 -->
            <a 
              v-if="siteConfig.resourceDocsUrl" 
              :href="siteConfig.resourceDocsUrl" 
              target="_blank" 
              class="text-gray-300 hover:text-white transition-colors duration-200"
            >
              资源文档
            </a>
            <!-- 交流群二维码 -->
            <div v-if="siteConfig.contactGroupImage" class="relative group">
              <button 
                class="text-gray-300 hover:text-white transition-colors duration-200"
                @click="showQrCodeModal = true"
              >
                交流群
              </button>
              <!-- 二维码弹窗 -->
              <div class="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-lg shadow-lg p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <img :src="siteConfig.contactGroupImage" alt="交流群二维码" class="w-full h-auto rounded object-contain max-h-40">
                <p class="text-xs text-gray-400 mt-2 text-center">扫码加入交流群</p>
              </div>
            </div>
            <button
              @click="toggleFilters"
              class="filter-button"
              :class="{ active: showFilters }"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"/>
              </svg>
              筛选
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- 移动端筛选抽屉 -->
    <div v-if="showFilters" class="md:hidden">
      <!-- 遮罩层 -->
      <div 
        class="fixed inset-0 bg-black bg-opacity-50 z-40"
        @click="toggleFilters"
      ></div>
      
      <!-- 抽屉内容 -->
      <div class="fixed bottom-0 left-0 right-0 bg-gray-800 rounded-t-2xl z-50 max-h-[80vh] overflow-y-auto">
        <!-- 抽屉头部 -->
        <div class="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 class="text-lg font-bold text-white">筛选条件</h2>
          <button @click="toggleFilters" class="text-gray-400 hover:text-white">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- 筛选内容 -->
        <div class="p-4 space-y-4">
          <!-- 频道筛选 -->
          <div class="filter-section">
            <label class="filter-label">频道</label>
            <div class="filter-options">
              <button
                v-for="channel in filterConfig.channels"
                :key="channel.value"
                @click="updateFilter('channel', channel.value)"
                class="filter-option"
                :class="{ active: filters.channel === channel.value }"
              >
                {{ channel.name }}
              </button>
            </div>
          </div>

          <!-- 地区筛选 -->
          <div v-if="filterConfig.areas.length > 1" class="filter-section">
            <label class="filter-label">地区</label>
            <div class="filter-options">
              <button
                v-for="area in filterConfig.areas"
                :key="area.value"
                @click="updateFilter('area', area.value)"
                class="filter-option"
                :class="{ active: filters.area === area.value }"
              >
                {{ area.name }}
              </button>
            </div>
          </div>

          <!-- 年份筛选 -->
          <div class="filter-section">
            <label class="filter-label">年份</label>
            <div class="filter-options">
              <button
                v-for="year in filterConfig.years"
                :key="year.value"
                @click="updateFilter('year', year.value)"
                class="filter-option"
                :class="{ active: filters.year === year.value }"
              >
                {{ year.name }}
              </button>
            </div>
          </div>

          <!-- 分类筛选 -->
          <div v-if="filterConfig.categories.length > 1" class="filter-section">
            <label class="filter-label">分类</label>
            <div class="filter-options">
              <button
                v-for="category in filterConfig.categories"
                :key="category.value"
                @click="updateFilter('cate', category.value)"
                class="filter-option"
                :class="{ active: filters.cate === category.value }"
              >
                {{ category.name }}
              </button>
            </div>
          </div>

          <!-- 榜单类型 -->
          <div class="filter-section">
            <label class="filter-label">榜单类型</label>
            <div class="filter-options">
              <button
                v-for="rankType in filterConfig.rankTypes"
                :key="rankType.value"
                @click="updateFilter('rank_type', rankType.value)"
                class="filter-option"
                :class="{ active: filters.rank_type === rankType.value }"
              >
                {{ rankType.name }}
              </button>
            </div>
          </div>
          
          <!-- 应用按钮 -->
          <div class="pt-4 border-t border-gray-700">
            <button
              @click="toggleFilters"
              class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium"
            >
              完成筛选
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 桌面端筛选面板 -->
    <div v-if="showFilters" class="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
      <!-- 频道筛选 -->
      <div class="filter-section">
        <label class="filter-label">频道</label>
        <div class="filter-options">
          <button
            v-for="channel in filterConfig.channels"
            :key="channel.value"
            @click="updateFilter('channel', channel.value)"
            class="filter-option"
            :class="{ active: filters.channel === channel.value }"
          >
            {{ channel.name }}
          </button>
        </div>
      </div>

      <!-- 地区筛选 -->
      <div v-if="filterConfig.areas.length > 1" class="filter-section">
        <label class="filter-label">地区</label>
        <div class="filter-options">
          <button
            v-for="area in filterConfig.areas"
            :key="area.value"
            @click="updateFilter('area', area.value)"
            class="filter-option"
            :class="{ active: filters.area === area.value }"
          >
            {{ area.name }}
          </button>
        </div>
      </div>

      <!-- 年份筛选 -->
      <div class="filter-section">
        <label class="filter-label">年份</label>
        <div class="filter-options">
          <button
            v-for="year in filterConfig.years"
            :key="year.value"
            @click="updateFilter('year', year.value)"
            class="filter-option"
            :class="{ active: filters.year === year.value }"
          >
            {{ year.name }}
          </button>
        </div>
      </div>

      <!-- 分类筛选 -->
      <div v-if="filterConfig.categories.length > 1" class="filter-section">
        <label class="filter-label">分类</label>
        <div class="filter-options">
          <button
            v-for="category in filterConfig.categories"
            :key="category.value"
            @click="updateFilter('cate', category.value)"
            class="filter-option"
            :class="{ active: filters.cate === category.value }"
          >
            {{ category.name }}
          </button>
        </div>
      </div>

      <!-- 榜单类型 -->
      <div class="filter-section">
        <label class="filter-label">榜单类型</label>
        <div class="filter-options">
          <button
            v-for="rankType in filterConfig.rankTypes"
            :key="rankType.value"
            @click="updateFilter('rank_type', rankType.value)"
            class="filter-option"
            :class="{ active: filters.rank_type === rankType.value }"
          >
            {{ rankType.name }}
          </button>
        </div>
      </div>
    </div>

    <!-- 主要内容 -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
      <!-- 每日更新模块 -->
      <div v-if="showTodayHotSection" class="mb-6 md:mb-8">
        <!-- 模块标题 -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center">
            <div class="w-1 h-6 bg-blue-500 rounded-full mr-3"></div>
            <h2 class="text-lg md:text-xl font-bold text-white">今日热搜资源</h2>
            <span v-if="dailyUpdateTime" class="ml-3 text-xs text-gray-500">{{ dailyUpdateTime }}</span>
          </div>
          <button 
            @click="goToDailyUpdate" 
            class="text-blue-400 hover:text-blue-300 text-sm flex items-center transition-colors"
          >
            查看全部
            <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
        
        <!-- 双栏卡片布局（固定最小高度避免页面跳动） -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 min-h-[320px]">
          <!-- 加载状态骨架屏 -->
          <template v-if="dailyLoading">
            <div class="bg-gray-800 rounded-xl p-4 border border-gray-700 animate-pulse">
              <div class="flex items-center mb-3">
                <div class="w-5 h-5 bg-gray-700 rounded mr-2"></div>
                <div class="h-4 bg-gray-700 rounded w-16"></div>
              </div>
              <div class="space-y-3">
                <div v-for="i in 6" :key="'skeleton-movie-' + i" class="flex items-center py-2">
                  <div class="w-6 h-6 bg-gray-700 rounded mr-3"></div>
                  <div class="h-4 bg-gray-700 rounded flex-1"></div>
                </div>
              </div>
            </div>
            <div class="bg-gray-800 rounded-xl p-4 border border-gray-700 animate-pulse">
              <div class="flex items-center mb-3">
                <div class="w-5 h-5 bg-gray-700 rounded mr-2"></div>
                <div class="h-4 bg-gray-700 rounded w-24"></div>
              </div>
              <div class="space-y-3">
                <div v-for="i in 6" :key="'skeleton-tv-' + i" class="flex items-center py-2">
                  <div class="w-6 h-6 bg-gray-700 rounded mr-3"></div>
                  <div class="h-4 bg-gray-700 rounded flex-1"></div>
                </div>
              </div>
            </div>
          </template>
          
          <!-- 实际内容 -->
          <template v-else>
          <!-- 电影栏 -->
          <div class="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div class="flex items-center mb-3">
              <svg class="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"/>
              </svg>
              <span class="text-white font-medium">电影</span>
              <span class="ml-2 text-xs text-gray-500">({{ dailyMovieResources.length }})</span>
            </div>
            <div class="space-y-2">
              <div 
                v-for="(item, index) in dailyMovieResources" 
                :key="'movie-' + index"
                @click="goToDailyWithSearch(item.title, 'movie')"
                class="flex items-center py-2 px-2 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors group"
              >
                <!-- 序号 -->
                <span 
                  class="w-6 h-6 flex items-center justify-center rounded text-xs font-bold mr-3 flex-shrink-0"
                  :class="{
                    'bg-red-500 text-white': index === 0,
                    'bg-orange-500 text-white': index === 1,
                    'bg-yellow-500 text-gray-900': index === 2,
                    'bg-gray-600 text-gray-300': index > 2
                  }"
                >
                  {{ index + 1 }}
                </span>
                <!-- 标题 -->
                <span class="text-gray-300 text-sm truncate group-hover:text-white transition-colors flex-1">
                  {{ item.title }}
                </span>
                <!-- 箭头 -->
                <svg class="w-4 h-4 text-gray-500 group-hover:text-blue-400 transition-colors flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
              <!-- 空状态 -->
              <div v-if="dailyMovieResources.length === 0" class="text-center py-4 text-gray-500 text-sm">
                暂无电影更新
              </div>
            </div>
          </div>
          
          <!-- 电视剧栏 -->
          <div class="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div class="flex items-center mb-3">
              <svg class="w-5 h-5 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <span class="text-white font-medium">电视剧/综艺</span>
              <span class="ml-2 text-xs text-gray-500">({{ dailyTvResources.length }})</span>
            </div>
            <div class="space-y-2">
              <div 
                v-for="(item, index) in dailyTvResources" 
                :key="'tv-' + index"
                @click="goToDailyWithSearch(item.title, 'tv')"
                class="flex items-center py-2 px-2 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors group"
              >
                <!-- 序号 -->
                <span 
                  class="w-6 h-6 flex items-center justify-center rounded text-xs font-bold mr-3 flex-shrink-0"
                  :class="{
                    'bg-red-500 text-white': index === 0,
                    'bg-orange-500 text-white': index === 1,
                    'bg-yellow-500 text-gray-900': index === 2,
                    'bg-gray-600 text-gray-300': index > 2
                  }"
                >
                  {{ index + 1 }}
                </span>
                <!-- 标题 -->
                <span class="text-gray-300 text-sm truncate group-hover:text-white transition-colors flex-1">
                  {{ item.title }}
                </span>
                <!-- 箭头 -->
                <svg class="w-4 h-4 text-gray-500 group-hover:text-purple-400 transition-colors flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
              <!-- 空状态 -->
              <div v-if="dailyTvResources.length === 0" class="text-center py-4 text-gray-500 text-sm">
                暂无电视剧更新
              </div>
            </div>
          </div>
          </template>
        </div>
      </div>

      <!-- 结果统计 -->
      <div class="mb-4 md:mb-6">
        <h2 class="text-lg md:text-2xl font-bold text-white mb-1 md:mb-2">
          {{ filters.channel }}网盘资源
        </h2>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="text-gray-400 mt-2">加载中...</p>
      </div>

      <!-- 资源网格 -->
      <div v-else class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6">
        <div
          v-for="resource in resources"
          :key="resource.id"
          class="movie-card group"
          @click="openResource(resource)"
        >
          <div class="relative overflow-hidden">
            <img
              :src="resource.src || '/placeholder-poster.jpg'"
              :alt="resource.title"
              class="movie-poster"
            />
            <!-- 悬浮遮罩层 - 明确搜索网盘资源 -->
            <div class="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center">
              <svg class="w-10 h-10 md:w-12 md:h-12 text-blue-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <span class="text-white text-sm md:text-base font-medium">搜索网盘资源</span>
              <span class="text-gray-300 text-xs mt-1">夸克/百度网盘</span>
            </div>
          </div>
          <div class="movie-info">
            <h3 class="movie-title">{{ resource.title }}</h3>
            <p class="movie-meta">{{ resource.actors }}</p>
            <div class="flex justify-between items-center mt-1 md:mt-2">
              <span class="text-gray-400 text-xs">{{ resource.year }} · {{ resource.area }}</span>
              <span v-if="resource.score" class="movie-rating text-xs">{{ resource.score }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 上拉加载提示 -->
      <div v-if="isLoadingMore" class="text-center py-4">
        <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <p class="text-gray-400 mt-2 text-sm">加载中...</p>
      </div>
      
      <!-- 没有更多数据提示 -->
      <div v-if="!hasMore && resources.length > 0" class="text-center py-4">
        <p class="text-gray-400 text-sm">没有更多数据了</p>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && resources.length === 0" class="text-center py-12 md:py-16">
        <svg class="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-gray-400 mt-3 md:mt-4">暂无数据</p>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import siteConfigService from '../services/siteConfig'
import announcementService from '../services/announcementService'
import dailyResourcesService from '../services/dailyResources'
import AnnouncementModal from '../components/AnnouncementModal.vue'
import { filterBlockedResources } from '../utils/resourceFilter'

const buildYearOptions = () => {
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 8 }, (_, index) => {
    const year = String(currentYear - index)
    return { name: year, value: year }
  })

  return [
    { name: '全部', value: '全部' },
    ...years,
    { name: '2010年代', value: '2010年代' },
    { name: '2000年代', value: '2000年代' },
    { name: '90年代', value: '90年代' },
    { name: '80年代', value: '80年代' },
    { name: '70年代', value: '70年代' },
    { name: '60年代', value: '60年代' }
  ]
}

export default {
  name: 'Home',
  components: {
    AnnouncementModal
  },
  data() {
    return {
      resources: [],
      loading: false,
      // 公告相关数据
      showAnnouncementModal: false,
      currentAnnouncement: null,
      announcementStats: {
        todayShowCount: 0,
        lastShowTime: null
      },
      filterLoading: false,
      searchQuery: '',
      showFilters: false,
      hasMore: false,
      start: 0,
      // 添加上拉加载相关状态
      isLoadingMore: false,
      scrollContainer: null,
      siteConfig: {
        siteName: '影视搜索',
        siteLogo: '',
        resourceDocsUrl: '',
        contactGroupImage: ''
      },
      showQrCodeModal: false,
      // 每日更新相关数据
      // 临时隐藏开关：仅隐藏入口，不删除代码
      showDailyUpdateEntry: true,
      showTodayHotSection: true,
      dailyMovieResources: [],
      dailyTvResources: [],
      dailyUpdateTime: '',
      dailyLoading: false,
      filters: {
        channel: '电视剧',
        area: '全部',
        year: '全部',
        cate: '全部',
        rank_type: '热搜榜'
      },
      filterConfig: {
        channels: [
          { name: '电影', value: '电影' },
          { name: '电视剧', value: '电视剧' },
          { name: '综艺', value: '综艺' },
          { name: '动漫', value: '动漫' },
          { name: '短剧', value: '短剧' }
        ],
        categories: [],
        areas: [],
        years: buildYearOptions(),
        rankTypes: [
          { name: '热搜榜', value: '热搜榜' },
          { name: '新片榜', value: '新片榜' },
          { name: '好评榜', value: '好评榜' }
        ]
      }
    }
  },
  async mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    
    // 加载网站配置
    this.loadSiteConfig()
    
    // 初始化筛选配置
    await this.fetchFilterConfig()
    this.searchResources(true)
    
    // 初始化上拉加载监听器
    this.$nextTick(() => {
      this.initScrollListener()
    })
    
    // 检查并展示公告
    await this.checkAndShowAnnouncement()
    
    // 加载每日更新资源（入口隐藏时不加载）
    if (this.showTodayHotSection) {
      this.loadDailyResources()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
    // 移除上拉加载监听器
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    async loadSiteConfig() {
      try {
        const config = await siteConfigService.getFrontendConfigs()
        if (config) {
          this.siteConfig.siteName = config.site_name || '影视搜索'
          // 直接使用后端返回的完整URL，无需额外处理
          this.siteConfig.siteLogo = config.site_logo || ''
          this.siteConfig.resourceDocsUrl = config.resource_docs_url || ''
          this.siteConfig.contactGroupImage = config.contact_group_image || ''
        }
      } catch (error) {
        console.error('加载网站配置失败:', error)
      }
    },
    checkScreenSize() {
      if (window.innerWidth >= 768) {
        this.showFilters = true
      }
    },
    toggleFilters() {
      this.showFilters = !this.showFilters
    },
    updateFilter(key, value) {
      this.filters[key] = value
      // 自动触发搜索
      this.searchResources(true)
    },
    async fetchFilterConfig() {
      try {
        const response = await axios.get(`/api/movie/filter-config`)
        if (response.data.status === 'success') {
          const data = response.data.data
          this.filterConfig = {
            channels: Array.isArray(data.channels) 
              ? data.channels.map(item => typeof item === 'string' ? { name: item, value: item } : item)
              : this.filterConfig.channels,
            categories: Array.isArray(data.categories) 
              ? data.categories.map(item => typeof item === 'string' ? { name: item, value: item } : item)
              : this.filterConfig.categories,
            areas: Array.isArray(data.areas) 
              ? data.areas.map(item => typeof item === 'string' ? { name: item, value: item } : item)
              : this.filterConfig.areas,
            years: Array.isArray(data.years) 
              ? data.years.map(item => typeof item === 'string' ? { name: item, value: item } : item)
              : this.filterConfig.years,
            rankTypes: Array.isArray(data.rankTypes) 
              ? data.rankTypes.map(item => typeof item === 'string' ? { name: item, value: item } : item)
              : this.filterConfig.rankTypes
          }
        }
      } catch (error) {
        console.error('获取筛选配置失败:', error)
      }
    },
    async searchResources(reset = false) {
      await this.searchResourcesWithFilters(this.filters, reset)
    },
    async searchResourcesWithFilters(filters, reset = false) {
      if (reset) {
        this.start = 0
        this.resources = []
      }
      
      this.loading = true
      
      try {
        const params = new URLSearchParams({
          channel: filters.channel,
          area: filters.area,
          year: filters.year,
          cate: filters.cate,
          rank_type: filters.rank_type,
          start: this.start.toString(),
          count: '20'
        })
        
        const response = await axios.get(`/api/movie/resources?${params}`)
        
        if (response.data.status === 'success') {
          const responseData = response.data.data || {}
          const responseResources = responseData.resources || []
          const pagination = responseData.pagination || {}
          const newResources = filterBlockedResources(responseResources)
          if (reset) {
            // 重置时直接赋值
            this.resources = newResources
          } else {
            // 增量加载时使用push避免重新渲染
            this.resources.push(...newResources)
          }
          this.hasMore = typeof pagination.hasMore === 'boolean'
            ? pagination.hasMore
            : responseResources.length === 20
          
          // 只有在成功获取数据后才更新start，避免重复加载相同数据
          const nextStart = Number(pagination.nextStart)
          if (!Number.isNaN(nextStart)) {
            this.start = nextStart
          } else if (responseResources.length > 0) {
            this.start += responseResources.length
          }
        }
      } catch (error) {
        console.error('搜索资源失败:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch(e) {
      e.preventDefault()
      
      if (!this.searchQuery.trim()) {
        this.start = 0
        this.searchResources(true)
        return
      }
      
      // 在新页面中打开搜索页面
      window.open(`/search?q=${encodeURIComponent(this.searchQuery.trim())}&disk_type=全部`, '_blank')
    },
    
    handleImageLoad(event) {
      // 图片加载完成后的处理，可以在这里添加额外的逻辑
      const img = event.target
      console.log('二维码图片加载完成，尺寸:', img.naturalWidth, 'x', img.naturalHeight)
    },
    // 初始化滚动监听器
    initScrollListener() {
      // 获取滚动容器（通常是window或main元素）
      this.scrollContainer = window
      this.scrollContainer.addEventListener('scroll', this.handleScroll)
    },
    
    // 处理滚动事件
    handleScroll() {
      if (this.isLoadingMore || !this.hasMore || this.loading) {
        return
      }
      
      // 计算距离底部的距离
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight
      const clientHeight = document.documentElement.clientHeight || document.body.clientHeight
      
      // 当距离底部小于200px时触发加载
      if (scrollHeight - scrollTop - clientHeight < 200) {
        this.loadMore()
      }
    },
    
    // 修改加载更多方法，使用增量加载避免重新渲染
    async loadMore() {
      if (this.isLoadingMore || !this.hasMore) {
        return
      }
      
      this.isLoadingMore = true
      const currentStart = this.start
      
      try {
        const params = new URLSearchParams({
          channel: this.filters.channel,
          area: this.filters.area,
          year: this.filters.year,
          cate: this.filters.cate,
          rank_type: this.filters.rank_type,
          start: currentStart.toString(),
          count: '20'
        })
        
        const response = await axios.get(`/api/movie/resources?${params}`)
        
        if (response.data.status === 'success') {
          const responseData = response.data.data || {}
          const responseResources = responseData.resources || []
          const pagination = responseData.pagination || {}
          const newResources = filterBlockedResources(responseResources)
          
          // 使用Vue.set或直接push来避免重新渲染整个数组
          this.resources.push(...newResources)
          this.hasMore = typeof pagination.hasMore === 'boolean'
            ? pagination.hasMore
            : responseResources.length === 20
          
          // 只有在成功获取数据后才更新start
          const nextStart = Number(pagination.nextStart)
          if (!Number.isNaN(nextStart)) {
            this.start = nextStart
          } else {
            this.start = currentStart + responseResources.length
          }
        }
      } catch (error) {
        console.error('加载更多失败:', error)
      } finally {
        this.isLoadingMore = false
      }
    },
    openResource(resource) {
      // 跳转到Search页面搜索该资源
      if (resource && resource.title) {
        // 在新页面中打开搜索页面，搜索该资源的标题
        window.open(`/search?q=${encodeURIComponent(resource.title.trim())}&disk_type=全部`, '_blank')
      }
    },
    
    // 公告相关方法
    async checkAndShowAnnouncement() {
    try {
      console.log('开始检查是否需要显示公告')
      // 检查是否应该展示公告
      const shouldShow = await announcementService.shouldShowAnnouncement()
      console.log('shouldShow结果:', shouldShow)
      
      if (shouldShow) {
        console.log('需要显示公告，获取当前公告')
        // 获取当前需要展示的公告
        const announcement = await announcementService.getCurrentAnnouncement()
        console.log('获取到的当前公告:', announcement)
        
        if (announcement) {
          console.log('显示公告弹窗')
          this.currentAnnouncement = announcement
          this.showAnnouncementModal = true
          
          // 更新展示统计
          announcementService.recordAnnouncementShow(announcement.id)
        } else {
          console.log('没有获取到当前公告')
        }
      } else {
        console.log('不需要显示公告')
      }
    } catch (error) {
      console.error('检查公告失败:', error)
    }
  },
    
    handleAnnouncementClose() {
      this.showAnnouncementModal = false
      this.currentAnnouncement = null
    },
    
    handleNeverShow() {
      // 标记不再显示此公告
      if (this.currentAnnouncement) {
        announcementService.markAnnouncementNeverShow(this.currentAnnouncement.id)
      }
      this.showAnnouncementModal = false
      this.currentAnnouncement = null
    },
    
    // 跳转到每日更新页面
    goToDailyUpdate() {
      this.$router.push('/daily-update')
    },
    
    // 加载每日更新资源
    async loadDailyResources() {
      this.dailyLoading = true
      try {
        // 并行加载电影和电视剧资源
        const [movieResult, tvResult] = await Promise.all([
          dailyResourcesService.getDailyResources('movie', ''),
          dailyResourcesService.getDailyResources('tv', '')
        ])
        
        if (movieResult.success) {
          // 只取前6个
          const filteredMovieResources = filterBlockedResources(movieResult.data.list || [])
          this.dailyMovieResources = filteredMovieResources.slice(0, 6)
          this.dailyUpdateTime = movieResult.data.updateTime || ''
        }
        
        if (tvResult.success) {
          // 只取前6个
          const filteredTvResources = filterBlockedResources(tvResult.data.list || [])
          this.dailyTvResources = filteredTvResources.slice(0, 6)
          if (!this.dailyUpdateTime && tvResult.data.updateTime) {
            this.dailyUpdateTime = tvResult.data.updateTime
          }
        }
      } catch (error) {
        console.error('加载每日更新资源失败:', error)
      } finally {
        this.dailyLoading = false
      }
    },
    
    // 点击每日更新标题，跳转到每日更新页面并带搜索参数
    goToDailyWithSearch(title, type) {
      this.$router.push({
        path: '/daily-update',
        query: { search: title, type: type }
      })
    }
  }
}
</script>

<style scoped>
/* 每日更新卡片淡入动画 */
.fade-slide-enter-active {
  transition: all 0.4s ease-out;
}
.fade-slide-leave-active {
  transition: all 0.2s ease-in;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(-10px);
}
.fade-slide-leave-to {
  opacity: 0;
}

.ai-assistant-entry {
  @apply relative inline-flex h-10 items-center gap-2 rounded-lg border border-blue-500 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 px-3.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 whitespace-nowrap;
  box-shadow: 0 10px 24px rgba(49, 130, 206, 0.26);
}

.ai-assistant-entry:hover {
  @apply -translate-y-0.5 border-blue-400 brightness-110 shadow-xl;
  box-shadow: 0 14px 30px rgba(49, 130, 206, 0.34);
}

.ai-assistant-entry:active {
  @apply translate-y-0 brightness-100;
}

.ai-assistant-entry:focus-visible,
.ai-assistant-entry-mobile:focus-visible {
  @apply outline-none ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-900;
}

.ai-assistant-entry-icon {
  @apply flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-white bg-opacity-20 text-white;
}

.ai-assistant-entry-badge {
  @apply rounded-md bg-white bg-opacity-20 px-2 py-0.5 text-xs font-medium text-blue-50;
}

.ai-assistant-entry-mobile {
  @apply inline-flex h-8 items-center gap-1.5 rounded-lg border border-blue-400 bg-gradient-to-r from-blue-600 to-cyan-500 px-2.5 text-xs font-semibold text-white shadow-lg transition-all duration-200 whitespace-nowrap;
  box-shadow: 0 8px 18px rgba(49, 130, 206, 0.3);
}

.ai-assistant-entry-mobile:hover {
  @apply brightness-110;
}

.ai-assistant-entry-mobile:active {
  @apply brightness-100;
}

@media (max-width: 1180px) {
  .ai-assistant-entry-badge {
    @apply hidden;
  }

  .ai-assistant-entry {
    @apply px-3;
  }
}

@media (max-width: 390px) {
  .ai-assistant-entry-mobile span {
    @apply hidden;
  }

  .ai-assistant-entry-mobile {
    @apply w-8 justify-center px-0;
  }
}
</style>
